/**
 * ApplicationStatusView — the read-only post-submission surface for the
 * applicant (applicant-application-flow spec, task 7).
 *
 * Rendered by `ApplyRendererSurface` for the read-only Application States —
 * `SUBMITTED` (awaiting review), `APPROVED`, and the terminal `REJECTED` — in
 * place of the editable `ApplicationFormContainer`. It exposes NO editable
 * inputs and NO Save/Submit controls (Req 6.2), states the current status with
 * copy that matches the resolved state (no stale banners — Req 6.3, 11.4), and
 * shows a read-only summary of what was submitted (division + ranked title
 * preferences, sourced from the saved response — Req 6.4). The `APPROVED` view
 * additionally surfaces the approved title when one is set (Req 7.1).
 *
 * It is presentational: the surface resolves the status + supplies the response
 * data, so this component performs no I/O and holds no editable state.
 *
 * The terminal `REJECTED` view is read-only and carries NO director-only
 * internal reject note: this component's props expose no `internalRejectNote`,
 * and the owner-selectable `ApplicationResponse` DTO deliberately does not carry
 * that field, so there is no path to render it to a contestant
 * (application-decision-feedback Req 5.2 — Internal_Reject_Note is director-only).
 *
 * Validates: Requirements 6.1, 6.2, 6.4, 7.1, 11.3, 11.4;
 *            application-decision-feedback Requirement 5.2
 */

import * as React from 'react';

export type ApplicationStatusKind = 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export interface ApplicationStatusViewProps {
  /** The resolved read-only status this view presents. */
  status: ApplicationStatusKind;
  /** The human division label for the applied-for division, when resolvable. */
  divisionLabel?: string | null;
  /** The applicant's saved ranked title preferences (read-only summary). */
  titlePreferences?: readonly string[] | null;
  /** ISO timestamp the application was submitted, when known. */
  submittedAt?: string | null;
  /** The director-set approved title, shown on the APPROVED view when present. */
  approvedTitle?: string | null;
}

interface StatusCopy {
  eyebrow: string;
  heading: string;
  body: string;
}

function copyFor(status: ApplicationStatusKind): StatusCopy {
  switch (status) {
    case 'APPROVED':
      return {
        eyebrow: 'Approved',
        heading: 'Your application has been approved',
        body: 'Congratulations! The pageant team has approved your application. Watch for next steps.',
      };
    case 'REJECTED':
      // Contestant-facing closure copy only. The director-only
      // Internal_Reject_Note is intentionally NOT surfaced here (Req 5.2).
      return {
        eyebrow: 'Not approved',
        heading: 'Your application was not approved',
        body: 'Your application was not approved this season. Please reach out to the pageant director if you have questions.',
      };
    case 'SUBMITTED':
    default:
      return {
        eyebrow: 'Under review',
        heading: 'Your application has been submitted',
        body: 'Your application is complete and under review. The pageant team will reach out with next steps.',
      };
  }
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The read-only status surface. Announced via `role="status"`; contains no
 * form controls so a submitted/decided application can never be re-edited or
 * re-submitted here (Req 6.2).
 */
export function ApplicationStatusView({
  status,
  divisionLabel,
  titlePreferences,
  submittedAt,
  approvedTitle,
}: ApplicationStatusViewProps): React.ReactElement {
  const copy = copyFor(status);
  const submittedOn = formatDate(submittedAt);
  const prefs = (titlePreferences ?? []).filter((p) => p && p.trim() !== '');

  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="application-status-view"
      data-status={status}
      className="mx-auto max-w-xl space-y-6 rounded-card border border-border bg-card p-8 shadow-subtle"
    >
      <header className="space-y-1.5">
        <p className="font-sans text-eyebrow-sm uppercase tracking-widest text-muted-foreground">
          {copy.eyebrow}
        </p>
        <h1 className="font-display text-page-title text-foreground">{copy.heading}</h1>
        <p className="font-sans text-body text-muted-foreground">{copy.body}</p>
      </header>

      <dl className="space-y-4 border-t border-border pt-5">
        {status === 'APPROVED' && approvedTitle ? (
          <div className="space-y-1">
            <dt className="font-sans text-body-sm font-semibold text-foreground">Approved title</dt>
            <dd className="font-sans text-body text-muted-foreground">{approvedTitle}</dd>
          </div>
        ) : null}

        {divisionLabel ? (
          <div className="space-y-1">
            <dt className="font-sans text-body-sm font-semibold text-foreground">Division</dt>
            <dd className="font-sans text-body text-muted-foreground">{divisionLabel}</dd>
          </div>
        ) : null}

        {prefs.length > 0 ? (
          <div className="space-y-1">
            <dt className="font-sans text-body-sm font-semibold text-foreground">
              Title preferences
            </dt>
            <dd>
              <ol className="list-decimal space-y-0.5 pl-5 font-sans text-body text-muted-foreground">
                {prefs.map((pref, index) => (
                  <li key={`${index}-${pref}`}>{pref}</li>
                ))}
              </ol>
            </dd>
          </div>
        ) : null}

        {submittedOn ? (
          <div className="space-y-1">
            <dt className="font-sans text-body-sm font-semibold text-foreground">Submitted</dt>
            <dd className="font-sans text-body text-muted-foreground">{submittedOn}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

export default ApplicationStatusView;
